import { Card } from "@/src/components/wrappers/Card";
import { Booking, OfferYacht, OperatorPricing } from "../../model";
import { Button } from "@/src/components/ui/button";
import { OperatorPricingCard } from "./OperatorPricingCard";
import { resolveOperatorCurrencyCode } from "../../operatorPricing";
import { useCallback, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../components/ui/form";
import { Input } from "../../../../components/ui/input";
import { useBookingCustomerPricingMutation } from "../../../../lib/bookingCustomerPricingMutation";
import { Currency } from "../../../Currencies/model";
import { useOffersStore } from "../../../../stores/offersStore";
import { useBookingData } from "@/src/hooks/useBookingData";
import { CrewedPricing } from "@/src/app/Offers/EditOffer/ChangeYachtDetailsModal/Pricing";

const PricingSection = ({ booking }: { booking: Booking }) => {
  const [customerPricingModalOpen, setCustomerPricingModalOpen] = useState(false);
  const { currencies } = useOffersStore();
  const { operatorData } = useBookingData(booking);
  const offerCurrencyCode = booking?.currency?.code;
  const yachtCurrencyCode = booking?.offerYacht.yacht?.amenities?.find((am) => !!am.currency)?.currency?.code || "EUR";
  const yachtCurrency = currencies?.find((c) => c.code === yachtCurrencyCode);
  const offerCurrency = currencies?.find((c) => c.code === (offerCurrencyCode || "EUR"));
  const operatorCurrencyCode = resolveOperatorCurrencyCode(booking, operatorData);

  const pricing = booking?.pricing;

  const customerPricing = {
    depositPrice: pricing?.depositPrice,
    discountCharterPrice: booking.discountCharterPrice,
    pricing: {
      vatPrice: pricing?.vatPrice,
      apaPrice: pricing?.apaPrice,
      apaPercentage: pricing?.apaPercentage,
      vatPercentage: pricing?.vatPercentage,
      deliveryFeesPrice: pricing?.deliveryFeesPrice,
      charterTerms: pricing?.charterTerms,
    },
  };

  const calculatedPricing = useMemo(() => {
    const amenities = booking?.offerYacht?.yacht?.amenities;

    const extras =
      booking?.amenitiesDetails
        ?.filter((am) => am.checked && !am.included)
        ?.map((am) => {
          const amenity = amenities?.find((a) => a.id === am.id);
          return {
            ...amenity,
            ...am,
          };
        }) ?? [];

    const locallyAddedExtras = extras?.filter((am) => am.paidLocally);
    const advancedAddedExtras = extras?.filter((am) => !am.paidLocally);
    const locallyAddedExtrasPrice = locallyAddedExtras?.reduce((acc, { price }) => acc + price!, 0) ?? 0;
    const advancedAddedExtrasPrice = advancedAddedExtras?.reduce((acc, { price }) => acc + price!, 0) ?? 0;

    const locallyAddedExtrasPriceInOfferCurrency = calculatePrice(
      offerCurrency!,
      yachtCurrency!,
      locallyAddedExtrasPrice,
    );

    const locallyAddedExtrasPriceInYachtCurrency = locallyAddedExtrasPrice;
    const vatPrice = pricing?.vatPrice ?? 0;
    const apaPrice = pricing?.apaPrice ?? 0;
    const deliveryFeesPrice = pricing?.deliveryFeesPrice ?? 0;
    // Build the payable total from its parts rather than from pricing.totalPrice.
    // That stored figure is a snapshot from when the offer was priced and is not
    // recomputed when the charter discount changes or an extra moves between "in
    // advance" and "paid locally", so it could contradict the rows listed above it.
    const totalPrice =
      (pricing?.clientPrice ?? 0) +
      vatPrice +
      apaPrice +
      deliveryFeesPrice +
      advancedAddedExtrasPrice +
      locallyAddedExtrasPriceInOfferCurrency;

    return {
      advancedAddedExtras,
      advancedAddedExtrasPrice,
      locallyAddedExtrasPrice,
      locallyAddedExtras,
      totalPrice,
      yachtCurrency,
      offerCurrency,
      depositPrice: pricing?.depositPrice,
      locallyAddedExtrasPriceInYachtCurrency,
      vatPrice,
      apaPrice,
      deliveryFeesPrice,
    };
  }, [currencies, booking]);
  return (
    <div className="flex w-full gap-6">
      <Card className="flex grow w-[546px] flex-col gap-2">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Customer pricing</h3>
          <Button onClick={() => setCustomerPricingModalOpen(true)} variant="link">
            Edit
          </Button>
          <EditCustomerPricingModal
            discountCharterPrice={booking.offerYacht.discountCharterPrice}
            offerYachtId={booking.offerYacht?.id}
            defaultValues={customerPricing}
            bookingId={booking.id}
            pricing={booking?.pricing}
            open={customerPricingModalOpen}
            onOpenChange={setCustomerPricingModalOpen}
            offerCurrencySymbol={booking?.currency?.symbol}
            crewed={booking?.offerYacht?.yacht?.type === "crewed"}
          />
        </div>
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="text-base">Discounted charter price</span>
            {/* <span className="text-xs text--gray-500">HELM's client price</span> */}
          </div>
          <div>
            <span className="text-base text--slate-900 font-medium">
              {formatPrice(pricing.clientPrice, booking.currency?.code)}
            </span>
          </div>
        </div>
        {!!pricing.vatPrice && (
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-base">VAT price</span>
            </div>
            <div>
              <span className="text-base text--slate-900 font-medium">
                {formatPrice(pricing.vatPrice, booking.currency?.code)}
              </span>
            </div>
          </div>
        )}
        {!!pricing.apaPrice && (
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-base">APA price</span>
            </div>
            <div>
              <span className="text-base text--slate-900 font-medium">
                {formatPrice(pricing.apaPrice, booking.currency?.code)}
              </span>
            </div>
          </div>
        )}
        {!!pricing.deliveryFeesPrice && (
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <span className="text-base">Delivery fees price</span>
            </div>
            <div>
              <span className="text-base text--slate-900 font-medium">
                {formatPrice(pricing.deliveryFeesPrice, booking.currency?.code)}
              </span>
            </div>
          </div>
        )}
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="text-base">Extras (in advance)</span>
            {calculatedPricing.advancedAddedExtras?.map((amenity, index) => (
              <span className="text-xs text--gray-500" key={index}>
                {amenity.text}
              </span>
            ))}
          </div>
          <div>
            <span className="text-base text--slate-900 font-medium">
              {formatPrice(calculatedPricing.advancedAddedExtrasPrice, booking.currency?.code)}
            </span>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="text-base">Extras (locally)</span>
            {calculatedPricing.locallyAddedExtras?.map((amenity, index) => (
              <span className="text-xs text--gray-500" key={index}>
                {amenity.text}
              </span>
            ))}
          </div>
          <div>
            <span className="text-base text--slate-900 font-medium">
              {formatPrice(calculatedPricing.locallyAddedExtrasPriceInYachtCurrency, yachtCurrencyCode)}
            </span>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="text-base font-semibold">Approx. total amount payable</span>
          </div>
          <div className="flex flex-col">
            <span className="text-base text--slate-900 font-medium">
              {formatPrice(calculatedPricing.totalPrice, offerCurrencyCode)}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex flex-col">
            <span className="text-base text--slate-500">Security deposit</span>
          </div>
          <div>
            <span className="text-base text--slate-500 font-medium">
              {formatPrice(calculatedPricing?.depositPrice, yachtCurrencyCode)}
            </span>
          </div>
        </div>
      </Card>
      <OperatorPricingCard booking={booking} operatorCurrencyCode={operatorCurrencyCode} tab="overview" />
    </div>
  );
};

export default PricingSection;

function formatPrice(price: number, code: string) {
  return new Intl.NumberFormat("en-EN", { style: "currency", currency: code }).format(price) || "N/A";
}

const pricingSchema = z.object({
  discountCharterPrice: z.number(),
  depositPrice: z.number(),
  pricing: z.object({
    vatPercentage: z.string().optional(),
    apaPercentage: z.string().optional(),
    vatPrice: z.number().optional(),
    apaPrice: z.number().optional(),
    deliveryFeesPrice: z.number().optional(),
    charterTerms: z.enum(["inclusive", "exclusive"]).optional(),
  }),
});

type CustomerPricingForm = z.infer<typeof pricingSchema>;

const EditCustomerPricingModal = ({
  open,
  onOpenChange,
  defaultValues,
  bookingId,
  offerYachtId,
  pricing,
  discountCharterPrice,
  offerCurrencySymbol,
  crewed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: {
    depositPrice: number;
    discountCharterPrice: number;
    pricing: {
      vatPrice?: number;
      apaPrice?: number;
      vatPercentage?: string;
      apaPercentage?: string;
      deliveryFeesPrice?: number;
      charterTerms?: "inclusive" | "exclusive";
    };
  };
  bookingId: string;
  pricing: OfferYacht["pricing"];
  offerYachtId: string;
  discountCharterPrice: number;
  offerCurrencySymbol: string;
  crewed: boolean;
}) => {
  const pricingMutation = useBookingCustomerPricingMutation(offerYachtId, bookingId);
  const form = useForm<CustomerPricingForm>({
    resolver: zodResolver(pricingSchema),
    defaultValues: defaultValues,
  });

  const vatPercentage = form.watch("pricing.vatPercentage");
  const apaPercentage = form.watch("pricing.apaPercentage");

  useEffect(() => {
    form.reset(defaultValues);
  }, [open]);

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        const diffPrice = discountCharterPrice - data.discountCharterPrice;
        const currentVatPrice = pricing.vatPrice ?? 0;
        const currentApaPrice = pricing.apaPrice ?? 0;
        const currentDeliveryFeesPrice = pricing.deliveryFeesPrice ?? 0;
        const extrasPrice =
          pricing.totalPrice - pricing.clientPrice - currentVatPrice - currentApaPrice - currentDeliveryFeesPrice;
        const totalDiscountPercentage =
          Math.round(100 - (data.discountCharterPrice / pricing.originalPrice) * 100) || 0;
        console.log(data, "dataa");
        const vatPrice = data.pricing?.vatPrice || 0;
        const apaPrice = data.pricing?.apaPrice || 0;
        const deliveryFeesPrice = data.pricing?.deliveryFeesPrice || 0;
        const totalPrice = data.discountCharterPrice + extrasPrice + vatPrice + apaPrice + deliveryFeesPrice;
        console.log({
          pricing: {
            ...pricing,
            vatPrice: data.pricing.vatPrice,
            apaPrice: data.pricing.apaPrice,
            deliveryFeesPrice: data.pricing.deliveryFeesPrice,
            vatPercentage: data.pricing.vatPercentage,
            apaPercentage: data.pricing.apaPercentage,
            depositPrice: data.depositPrice,
            totalPrice,
            discountPrice: pricing.discountPrice + diffPrice,
            discountPercentage: totalDiscountPercentage,
            helmCustomDiscountPrice: pricing.helmCustomDiscountPrice + diffPrice,
            clientPrice: data.discountCharterPrice,
          },
          discountCharterPrice: data.discountCharterPrice,
        });
        pricingMutation.mutate({
          pricing: {
            ...pricing,
            vatPrice: data.pricing.vatPrice,
            apaPrice: data.pricing.apaPrice,
            deliveryFeesPrice: data.pricing.deliveryFeesPrice,
            vatPercentage: data.pricing.vatPercentage,
            apaPercentage: data.pricing.apaPercentage,
            depositPrice: data.depositPrice,
            charterTerms: data.pricing.charterTerms,
            totalPrice,
            discountPrice: pricing.discountPrice + diffPrice,
            discountPercentage: totalDiscountPercentage,
            helmCustomDiscountPrice: pricing.helmCustomDiscountPrice + diffPrice,
            clientPrice: data.discountCharterPrice,
          },
          discountCharterPrice: data.discountCharterPrice,
        });
        onOpenChange(false);
      },
      () => {
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Edit customer pricing</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <FormField
              name="discountCharterPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Discounted charter price</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => {
                        const val = +e.target.value;
                        form.setValue(
                          "pricing.vatPrice",
                          !!vatPercentage ? Math.round(Number((+vatPercentage / 100) * val)) : undefined,
                        );
                        form.setValue(
                          "pricing.apaPrice",
                          !!apaPercentage ? Math.round(Number((+apaPercentage / 100) * val)) : undefined,
                        );
                        field.onChange(val);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="depositPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Security deposit</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onChange={(e) => field.onChange(+e.target.value)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {crewed && <CrewedPricing form={form} offerCurrencySymbol={offerCurrencySymbol} />}
        </Form>
        <DialogFooter>
          <Button onClick={onSubmit} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const calculatePrice = (toCurrency: Currency, fromCurrency: Currency, amount: number) => {
  const rate = toCurrency?.rate / fromCurrency?.rate;
  const price = roundWithPrecision(amount * rate, toCurrency?.precisionDigits);
  return price;
};

function roundWithPrecision(value: number, precision: number) {
  return Math.round(value * Math.pow(10, -precision)) / Math.pow(10, -precision);
}
